/**
 * This file contains the logic for measuring and pushing web vitals scores into the datalayer.
 */

/* Stores all vitals measures on the page */
const vitals = {};
const inputVitals = {};
let pushedInputToDataLayer = false;
let domLoadedTimeSet = false;
let domLoadedTimeValue = 0;

// TTI calculation variables
let longTasks = [];
let fcpTime = 0;
let networkRequestCount = 0;
let networkRequests = new Map();
let ttiCalculated = false;

/**
 * @desc Handles the logic for the output of the observers.
 */
const handleVitals = (name, value, eventType) => {
  switch (eventType) {
    case "web_vitals":
      vitals[name] = value;
      if (name === 'DOMLoadedTime') {
        domLoadedTimeSet = true;
        domLoadedTimeValue = value;
      }
      if (name === 'FCP') {
        fcpTime = parseFloat(value);
      }
      break;
    case "performance_input_web_vitals":
      if (!inputVitals[name]) {
        inputVitals[name] = value;
      }
      if (Object.keys(inputVitals).length === 2 && !pushedInputToDataLayer) {
        window.dataLayer.push({
          event: eventType,
          data_vitals: inputVitals
        });
        pushedInputToDataLayer = true;
      }
      break;
    default:
      break;
  }
};

/**
 * Get the connection information from the User.
 * @returns { Object } - An object with the Network/Connection information from the user.
 */
const getNetworkInformation = () => {
  let networkInfo = {};
  const { connection } = navigator;
  if (connection) {
    networkInfo = {
      downlink: connection.downlink ? connection.downlink : "",
      downlink_max: connection.downlinkMax ? connection.downlinkMax : "",
      effective_type: connection.effectiveType ? connection.effectiveType : "",
      round_trip_time: connection.rtt ? connection.rtt : "",
      save_data: connection.saveData !== undefined ? connection.saveData : "",
      type: connection.type ? connection.type : ""
    };
  }
  return networkInfo;
};

/**
 * Track network requests
 */
const setupNetworkTracking = () => {
  let requestId = 0;
  
  const originalFetch = window.fetch;
  window.fetch = function() {
    const currentId = requestId++;
    networkRequestCount++;
    networkRequests.set(currentId, performance.now());
    
    return originalFetch.apply(this, arguments)
      .then(response => {
        networkRequestCount--;
        networkRequests.delete(currentId);
        return response;
      })
      .catch(error => {
        networkRequestCount--;
        networkRequests.delete(currentId);
        throw error;
      });
  };
  
  // Monitor XMLHttpRequest
  const originalOpen = XMLHttpRequest.prototype.open;
  const originalSend = XMLHttpRequest.prototype.send;
  
  XMLHttpRequest.prototype.open = function() {
    this._ttiRequestId = requestId++;
    return originalOpen.apply(this, arguments);
  };
  
  XMLHttpRequest.prototype.send = function() {
    networkRequestCount++;
    networkRequests.set(this._ttiRequestId, performance.now());
    
    this.addEventListener('readystatechange', () => {
      if (this.readyState === 4) { // request completed
        networkRequestCount--;
        networkRequests.delete(this._ttiRequestId);
      }
    });
    
    return originalSend.apply(this, arguments);
  };
};

/**
 * Calculate Time to Interactive (TTI) using a more accurate method
 * Following principles from Lighthouse's TTI calculation:
 * - Start from First Contentful Paint (FCP)
 * - Find first "quiet window" (no long tasks, max 2 network requests)
 * - Trace back to last long task
 * @returns {number} Time to Interactive in milliseconds
 */
const calculateTTI = () => {
  if (ttiCalculated || fcpTime === 0) {
    return vitals.TTI || 0;
  }
  
  // We need FCP for TTI calculation
  if (!fcpTime) {
    // Fallback to estimated FCP
    const navigationTiming = performance.getEntriesByType('navigation')[0];
    if (navigationTiming) {
      fcpTime = navigationTiming.domContentLoadedEventEnd;
    } else {
      return 0;
    }
  }
  
  const sortedTasks = [...longTasks].sort((a, b) => a.startTime - b.startTime);
  const tasksAfterFCP = sortedTasks.filter(task => task.startTime >= fcpTime);
  
  if (tasksAfterFCP.length === 0 && networkRequestCount <= 2) {
    ttiCalculated = true;
    return fcpTime;
  }
  
  const QUIET_WINDOW = 5000;
  let quietWindowStart = 0;
  
  let checkTime = fcpTime;
  
  if (tasksAfterFCP.length > 0) {
    const lastTask = tasksAfterFCP[tasksAfterFCP.length - 1];
    checkTime = lastTask.startTime + lastTask.duration;
  }

  const now = performance.now();
  if ((now - checkTime >= QUIET_WINDOW) && networkRequestCount <= 2) {
    quietWindowStart = checkTime;
  }
  
  if (quietWindowStart > 0) {
    const lastTaskBeforeQuiet = sortedTasks
      .filter(task => task.startTime + task.duration <= quietWindowStart)
      .pop();
    
    if (lastTaskBeforeQuiet) {
      const tti = lastTaskBeforeQuiet.startTime + lastTaskBeforeQuiet.duration;
      ttiCalculated = true;
      return Math.max(tti, fcpTime);
    } else {
      ttiCalculated = true;
      return fcpTime;
    }
  }
  
  const navigationTiming = performance.getEntriesByType('navigation')[0];
  const domContentLoaded = navigationTiming.domContentLoadedEventEnd;
  const firstInput = performance.getEntriesByType('first-input')[0];
  
  if (firstInput) {
    return firstInput.startTime;
  }
  
  return domContentLoaded;
};

/**
 * Get DOMLoadedTime from performance timing
 * @returns {number} DOMLoadedTime in milliseconds
 */
const getDOMLoadedTime = () => {
  try {
    const navigationEntries = performance.getEntriesByType('navigation');
    if (navigationEntries && navigationEntries.length > 0) {
      const navigationTiming = navigationEntries[0];
      return Math.round(navigationTiming.domContentLoadedEventEnd);
    }
    
    if (performance.getEntriesByType && performance.getEntriesByType('navigation').length > 0) {
      const navEntry = performance.getEntriesByType('navigation')[0];
      return Math.round(navEntry.domContentLoadedEventEnd);
    }
    
    // Last resort fallback
    return Math.round(performance.now());
  } catch (e) {
    console.error('Error calculating DOMLoadedTime:', e);
  }
  return 0;
};

/**
 * Get the current page URL information
 * @returns {Object} URL information
 */
const getUrlInfo = () => {
  return {
    full_url: window.location.href,
    url_without_query: window.location.origin + window.location.pathname
  };
};

/**
 * @desc Define and executes the observer for the web vitals.
 */
const executeVitalsListeners = () => {
  // Track long tasks for TTI calculation
  try {
    const longTaskObserver = new PerformanceObserver(entries => {
      entries.getEntries().forEach(entry => {
        longTasks.push({
          startTime: entry.startTime,
          duration: entry.duration
        });
      });
    });
    
    longTaskObserver.observe({ entryTypes: ['longtask'] });
  } catch (e) {
    console.warn('Long Task API not supported', e);
  }
  
  setupNetworkTracking();
  
  let domContentLoadedTime = 0;
  
  document.addEventListener('DOMContentLoaded', () => {
    domContentLoadedTime = performance.now();
    domLoadedTimeValue = domContentLoadedTime;
    domLoadedTimeSet = true;
    vitals.DOMLoadedTime = domContentLoadedTime;
    
    const apiDomLoadedTime = getDOMLoadedTime();
    if (apiDomLoadedTime > 0 && !domLoadedTimeValue) {
      domLoadedTimeValue = apiDomLoadedTime;
      domLoadedTimeSet = true;
      vitals.DOMLoadedTime = apiDomLoadedTime;
    }
  });

  const performanceObserver = new PerformanceObserver(entryList => {
    entryList.getEntries().forEach(entry => {
      if (entry.entryType) {
        switch (entry.entryType) {
          case "layout-shift":
            handleVitals("CLS", entry.value, "web_vitals");
            break;
          case "first-input":
            handleVitals(
              "FID",
              `${entry.duration}`,
              "performance_input_web_vitals"
            );
            break;
          case "largest-contentful-paint":
            handleVitals("LCP", `${entry.startTime}`, "web_vitals");
            break;
          case "paint":
            if (entry.name === "first-contentful-paint") {
              handleVitals(
                "FCP",
                `${entry.startTime}`,
                "web_vitals"
              );
              fcpTime = entry.startTime;
            }
            break;
          case "navigation": {
            const [pageNav] = entryList.getEntriesByType("navigation");
            handleVitals(
              "TTFB",
              `${pageNav.responseStart}`,
              "web_vitals"
            );
            
            if (pageNav.domContentLoadedEventEnd) {
              const domLoadTime = Math.round(pageNav.domContentLoadedEventEnd);
              if (domLoadTime > 0) {
                vitals.DOMLoadedTime = domLoadTime;
                domLoadedTimeSet = true;
                domLoadedTimeValue = domLoadTime;
              }
            }
            break;
          }
          case "event":
            if (entry.interactionId) {
              handleVitals(
                "INP",
                `${entry.duration}`,
                "performance_input_web_vitals"
              );
            }
            break;
          default:
            break;
        }
      }
    });
  });

  // Observe all relevant types
  performanceObserver.observe({
    type: "layout-shift",
    buffered: true
  });

  performanceObserver.observe({
    type: "first-input",
    buffered: true
  });

  performanceObserver.observe({
    type: "largest-contentful-paint",
    buffered: true
  });

  performanceObserver.observe({
    type: "paint",
    buffered: true
  });

  performanceObserver.observe({
    type: "navigation",
    buffered: true
  });

  performanceObserver.observe({
    type: "event",
    buffered: true,
    durationThreshold: 0
  });

  getNetworkInformation();

  window.addEventListener("load", () => {
    setTimeout(() => {
      const tti = calculateTTI();
      vitals.TTI = tti;

      if (!domLoadedTimeSet || domLoadedTimeValue === 0) {
        const apiDomLoadedTime = getDOMLoadedTime();
        if (apiDomLoadedTime > 0) {
          domLoadedTimeValue = apiDomLoadedTime;
          domLoadedTimeSet = true;
          vitals.DOMLoadedTime = apiDomLoadedTime;
        } else if (domContentLoadedTime > 0) {
          domLoadedTimeValue = domContentLoadedTime;
          domLoadedTimeSet = true;
          vitals.DOMLoadedTime = domContentLoadedTime;
        } else {
          const ttfb = parseFloat(vitals.TTFB) || 0;
          const lcp = parseFloat(vitals.LCP) || 0;
          if (ttfb > 0 && lcp > 0) {
            domLoadedTimeValue = Math.round((ttfb + lcp) / 2);
            domLoadedTimeSet = true;
            vitals.DOMLoadedTime = domLoadedTimeValue;
          }
        }
      }

      const urlInfo = getUrlInfo();

      window.dataLayer.push({
        event: "web_vitals",
        LCP: vitals.LCP || 0,
        TTFB: vitals.TTFB || 0,
        CLS: vitals.CLS || 0,
        TTI: vitals.TTI || 0,
        DOMLoadedTime: domLoadedTimeValue || 0,
        full_url: urlInfo.full_url,
        url_without_query: urlInfo.url_without_query
      });
    }, 1000);
  });
};

executeVitalsListeners();
